import React, { useEffect, useState } from 'react';
import { AiOutlineClose } from "react-icons/ai";
import { FiMessageCircle } from "react-icons/fi";
import useOutsideClick from "hooks/click-outside-hook";
import ChatModal from "./ChatModal";

interface TicketData {
  id?: number;
  event?: {
    title?: string;
    entryFee?: number;
  };
  host?: {
    user?: {
      firstName?: string;
      lastName?: string;
    };
    businessName?: string;
  };
  reason?: string;
  penaltyAmount?: number;
  status?: string;
  adminNotes?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticket: TicketData | null;
  onApprove: () => void;
  onReject: () => void;
  showApproveConfirmation?: boolean;
  onApproveWithPenalty?: (adminNotes: string) => void;
  onApproveWithoutPenalty?: (adminNotes: string) => void;
  onChat?: () => void;
}

const ConfirmationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ticket,
  onApprove,
  onReject,
  showApproveConfirmation = false,
  onApproveWithPenalty,
  onApproveWithoutPenalty,
  onChat,
}) => {
  const modalRef = useOutsideClick(onClose);
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [showChat, setShowChat] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setAdminNotes("");
      setShowChat(false);
    }
  }, [isOpen]);

  const handleChatClick = () => {
    setShowChat(true);
    if (onChat) {
      onChat();
    }
  };

  if (!isOpen || !ticket) {
    return null;
  }

  const hostName = ticket?.host?.user
    ? `${ticket.host.user.firstName || ""} ${ticket.host.user.lastName || ""}`.trim() || ticket?.host?.businessName
    : ticket?.host?.businessName || "-";

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  // Show approve confirmation modal
  if (showApproveConfirmation) {
    return (
      <div 
        className="fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          ref={modalRef}
          className="relative bg-white p-5 rounded-lg shadow-lg max-w-md w-[90%] transform transition-transform duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
          >
            <AiOutlineClose size={20} />
          </button>

          {/* Modal Header */}
          <div className="mb-4">
            <h2 className="text-xl text-center text-darkCharcoalText font-bold mb-2">
              Confirm Approval
            </h2>
            <p className="text-gray-600 text-sm text-center">
              Please choose how you want to approve this cancellation request
            </p>
          </div>

          {/* Modal Content */}
          <div className="space-y-3 mb-4">
            <div className="bg-coolLightGrayBg p-4 rounded-lg border border-lightGrayBorder">
              <p className="text-sm text-gray-600 mb-2 font-medium">Event Name</p>
              <p className="text-base font-semibold text-primaryText">
                {ticket?.event?.title || "-"}
              </p>
            </div>

            {/* Admin Notes Input */}
            <div className="flex flex-col gap-1">
              <label className="text-labelText text-sm font-semibold">
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Enter admin notes (optional)"
                   className="w-full min-h-[100px] py-2 px-4 border border-lightGrayBorder rounded-lg outline-none"
                rows={4}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                if (onApproveWithPenalty) {
                  onApproveWithPenalty(adminNotes);
                }
              }}
              className="w-full px-4 py-2 bg-darkTealBlue  text-white rounded-lg font-semibold transition-colors duration-200 h-[40px]"
            >
              Approve with Penalty
            </button>
            <button
              onClick={() => {
                if (onApproveWithoutPenalty) {
                  onApproveWithoutPenalty(adminNotes);
                }
              }}
              className="w-full px-4 py-2 bg-darkTealBlue  text-white rounded-lg font-semibold transition-colors duration-200 h-[40px]"
            >
              Approve without Penalty
            </button>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors duration-200 h-[40px]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show Chat Modal if chat is open
  if (showChat) {
    return (
      <ChatModal
        isOpen={showChat}
        onClose={() => {
          setShowChat(false);
          onClose();
        }}
        ticket={ticket}
        onBack={() => setShowChat(false)}
      />
    );
  }

  // Main modal showing event details
  return (
    <div 
      className="fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="relative bg-white p-5 rounded-lg shadow-lg max-w-2xl w-[90%] max-h-[90vh] overflow-y-auto transform transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition z-10"
        >
          <AiOutlineClose size={20} />
        </button>

        {/* Modal Header */}
        <div className="mb-4 pb-4 border-b border-mediumGrayBorder">
          <h2 className="text-xl text-center text-darkCharcoalText font-bold">
            Cancel Event Request Details
          </h2>
        </div>

        {/* Modal Content */}
        <div className="space-y-4 mb-6">
          {/* Event Name */}
          <div className="bg-coolLightGrayBg p-4 rounded-lg border border-lightGrayBorder">
            <p className="text-sm text-gray-600 mb-2 font-medium">Event Name</p>
            <p className="text-base font-semibold text-primaryText">
              {ticket?.event?.title || "-"}
            </p>
          </div>

          {/* Reason */}
          <div className="bg-coolLightGrayBg p-4 rounded-lg border border-lightGrayBorder">
            <p className="text-sm text-gray-600 mb-2 font-medium">Reason</p>
            <p className="text-base text-primaryText whitespace-pre-wrap">
              {ticket?.reason || "-"}
            </p>
          </div>

          {/* Entry Fee */}
          <div className="bg-coolLightGrayBg p-4 rounded-lg border border-lightGrayBorder">
            <p className="text-sm text-gray-600 mb-2 font-medium">Entry Fee</p>
            <p className="text-lg font-bold text-primaryText">
              {formatCurrency(ticket?.event?.entryFee)}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-mediumGrayBorder">
          {ticket?.status === "pending" && (
            <button
              onClick={onApprove}
              className="flex-1 px-4 py-2 bg-darkTealBlue text-white rounded-lg font-semibold transition-colors duration-200 h-[40px]"
            >
              Approve
            </button>
          )}

            <button
              onClick={handleChatClick}
              className="flex-1 px-4 py-2 bg-darkTealBlue text-white rounded-lg font-semibold transition-colors duration-200 h-[40px] flex items-center justify-center gap-2"
            >
              <FiMessageCircle size={18} />
              Chat
            </button>
    
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors duration-200 h-[40px]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
